/**
 * Generate a stable, unique ID for form elements
 */
export const generateFieldId = (label, prefix = 'field') => {
  return `${prefix}-${label?.replace(/[^a-zA-Z0-9]/g, '-')?.toLowerCase()}`;
};

/**
 * Build aria-describedby value linking input to error and/or description elements
 */
export const buildAriaDescribedBy = (fieldId, { hasError, hasDescription, hasHint } = {}) => {
  const ids = [];
  if (hasError) ids?.push(`${fieldId}-error`);
  if (hasDescription) ids?.push(`${fieldId}-description`);
  if (hasHint) ids?.push(`${fieldId}-hint`);
  return ids?.length > 0 ? ids?.join(' ') : undefined;
};

/**
 * Returns ARIA props for a form input field
 */
export const getInputAriaProps = ({
  fieldId,
  label,
  required = false,
  error = null,
  description = null,
  hint = null
}) => {
  return {
    id: fieldId,
    'aria-label': label,
    'aria-required': required ? 'true' : undefined,
    'aria-invalid': error ? 'true' : 'false',
    'aria-describedby': buildAriaDescribedBy(fieldId, {
      hasError: !!error,
      hasDescription: !!description,
      hasHint: !!hint
    }) || undefined
  };
};

/**
 * Announce a message to screen readers via a live region
 * Creates a temporary element if no live region exists
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const regionId = priority === 'assertive' ? 'sr-assertive-region' : 'sr-polite-region';
  let region = document.getElementById(regionId);

  if (!region) {
    region = document.createElement('div');
    region.id = regionId;
    region?.setAttribute('role', 'status');
    region?.setAttribute('aria-live', priority);
    region?.setAttribute('aria-atomic', 'true');
    region.className = 'sr-only';
    document.body?.appendChild(region);
  }

  // Clear then set to trigger re-announcement
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
};

/**
 * Keyboard navigation helper: trap focus within a container
 */
export const trapFocus = (containerRef) => {
  if (!containerRef?.current) return;

  const focusable = containerRef?.current?.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabIndex="-1"])'
  );

  const first = focusable?.[0];
  const last = focusable?.[focusable?.length - 1];

  const handleKeyDown = (e) => {
    if (e?.key !== 'Tab') return;
    if (e?.shiftKey) {
      if (document.activeElement === first) {
        e?.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e?.preventDefault();
        first?.focus();
      }
    }
  };

  containerRef?.current?.addEventListener('keydown', handleKeyDown);
  return () => containerRef?.current?.removeEventListener('keydown', handleKeyDown);
};

/**
 * Returns visible focus indicator classes
 */
export const focusRingClasses = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';

export default {
  generateFieldId,
  buildAriaDescribedBy,
  getInputAriaProps,
  announceToScreenReader,
  trapFocus,
  focusRingClasses
};
